import React, { FC, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { BaseBackResponsePagination } from '@savgroup-front-common/types';

import {
  safeFormattedIntlString,
  SafeFormattedMessageWithoutSpread,
} from '../../formatters';
import {
  ChevronFastLeftIcon,
  ChevronFastRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '../../protons/icons';
import { Input } from '../Form';

import messages from './messages';
import {
  $ButtonGroupWrapper,
  $ListPaginationButton,
  $ListPaginationInputText,
  $ListPaginationInterval,
  $ListPaginationIntervalWrapper,
  $ListPaginationPage,
  $ListPaginationWrapper,
  $PaginationPerPage,
  $Spacer,
} from './Pagination.styles';

interface PaginationProps {
  paginationInterval?: number[];
  onSelectInterval?: (interval: number) => void;
  onChangePage: (value: number) => void;
  pagination: BaseBackResponsePagination;
  dataTestId?: string;
  className?: string;
}

const Pagination: FC<PaginationProps> = ({
  onChangePage,
  onSelectInterval,
  pagination,
  paginationInterval,
  dataTestId,
  className,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(
    Number(pagination.page),
  );
  const [pagesNumber, setPagesNumber] = useState<number>(
    Number(pagination.pageCount),
  );

  const formContext = useForm();
  const { control } = formContext;

  useEffect(() => {
    if (pagination.pageCount !== pagesNumber) {
      if (currentPage > pagination.pageCount) {
        setCurrentPage(pagination.pageCount);
        onChangePage(pagination.pageCount);
      }
      setPagesNumber(pagination.pageCount);
    }
  }, [
    pagesNumber,
    onChangePage,
    pagination.page,
    pagination.pageCount,
    currentPage,
  ]);

  const disablePrev = currentPage <= 1;
  const disableNext = currentPage >= pagesNumber;

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      onChangePage(page);
    },
    [onChangePage],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nbr = Number(event.target.value);

      if (nbr === 0) {
        return setCurrentPage(1);
      }

      if (currentPage && (nbr < 1 || nbr > pagesNumber)) {
        return event.preventDefault();
      }

      setCurrentPage(nbr);

      return onChangePage(nbr);
    },
    [onChangePage, pagesNumber, currentPage],
  );

  const filteredPaginationInterval =
    paginationInterval?.filter((interval) => pagination.total >= interval) ??
    [];

  return (
    <$ListPaginationWrapper className={className} data-testid={dataTestId}>
      <$ListPaginationIntervalWrapper>
        {filteredPaginationInterval?.length > 0 &&
          filteredPaginationInterval.map((interval) => (
            <$ListPaginationInterval
              type={BUTTON_TYPES.BUTTON}
              key={interval}
              onClick={() => {
                if (onSelectInterval) {
                  onChangePage(1);
                  onSelectInterval(interval);
                }
              }}
              $isActive={interval === pagination.pageSize}
              data-testid={`${dataTestId}_listPaginationInterval_${interval}`}
            >
              {interval}
            </$ListPaginationInterval>
          ))}
      </$ListPaginationIntervalWrapper>

      <div data-testid={`${dataTestId}_listPaginationInterval_result_number`}>
        {pagination.total > 1 && pagination.total >= pagination.pageSize && (
          <$PaginationPerPage>
            <SafeFormattedMessageWithoutSpread
              message={messages.paginationPerPage}
            />
          </$PaginationPerPage>
        )}

        {pagination.total <= 1 && pagination.total >= pagination.pageSize && (
          <$PaginationPerPage>
            <SafeFormattedMessageWithoutSpread
              message={messages.paginationPerPageSingular}
            />
          </$PaginationPerPage>
        )}
      </div>
      <$Spacer />
      {pagesNumber > 1 && (
        <$ListPaginationPage
          data-testid={`${dataTestId}_listPaginationPage_container`}
        >
          <Controller
            name="paginationValue"
            control={control}
            render={({ field }) => (
              <Input
                name={field.name}
                min={0}
                max={pagination.total}
                isDisabled={pagesNumber === 1}
                onChange={handleChange}
                value={currentPage}
                selectAllOnFocus
                data-testid={`${dataTestId}_paginationValue`}
                placeholder={safeFormattedIntlString(messages.choosePage)}
                aria-labelledby={safeFormattedIntlString(messages.choosePage)}
              />
            )}
          />
          <$ListPaginationInputText
            data-testid={`${dataTestId}_listPaginationInputText_pagesNumber`}
          >
            <SafeFormattedMessageWithoutSpread
              message={messages.totalPage}
              values={{ totalPage: pagesNumber }}
            />
          </$ListPaginationInputText>
          <$ButtonGroupWrapper>
            <$ListPaginationButton
              type={BUTTON_TYPES.BUTTON}
              small
              hollow
              onClick={() => handlePageChange(1)}
              dataTestId={`${dataTestId}_first_page`}
              disabled={disablePrev}
              ariaLabel={messages.goToFirstPage}
            >
              <ChevronFastLeftIcon />
            </$ListPaginationButton>
            <$ListPaginationButton
              type={BUTTON_TYPES.BUTTON}
              small
              hollow
              disabled={disablePrev}
              onClick={() => {
                if (currentPage >= 2) {
                  handlePageChange(currentPage - 1);
                }
              }}
              dataTestId={`${dataTestId}_previous_page`}
              ariaLabel={messages.goToPreviousPage}
            >
              <ChevronLeftIcon size="20px" />
            </$ListPaginationButton>

            <$ListPaginationButton
              type={BUTTON_TYPES.BUTTON}
              small
              hollow
              disabled={disableNext}
              onClick={() => {
                if (currentPage < pagesNumber) {
                  handlePageChange(currentPage + 1);
                }
              }}
              dataTestId={`${dataTestId}_next_page`}
              ariaLabel={messages.goToNextPage}
            >
              <ChevronRightIcon size="20px" />
            </$ListPaginationButton>
            <$ListPaginationButton
              type={BUTTON_TYPES.BUTTON}
              small
              hollow
              onClick={() => handlePageChange(pagesNumber)}
              dataTestId={`${dataTestId}_last_page`}
              disabled={disableNext}
              ariaLabel={messages.goToLastPage}
            >
              <ChevronFastRightIcon />
            </$ListPaginationButton>
          </$ButtonGroupWrapper>
        </$ListPaginationPage>
      )}
    </$ListPaginationWrapper>
  );
};

export default Pagination;
